import { RefObject, useCallback, useEffect, useRef } from 'react';

import { Dayjs } from 'dayjs';

import { clampDateIndex, getNextVisibleDateRange } from '../../helpers/scroll/planner-scroll';
import { VisibleDateRange } from '../../types';

interface UsePlannerViewportStateParams {
  parentRef: RefObject<HTMLDivElement | null>;
  cellWidthPx: number;
  dates: Dayjs[];
  isLoading: boolean;
  isDragResizeInteracting: boolean;
  visibleRowsLength: number;
  setVisibleDateRange: (range: VisibleDateRange) => void;
  setFirstVisibleDate: (date: Dayjs | null) => void;
  isDragResizeInteractionActive: () => boolean;
}

interface UsePlannerViewportStateResult {
  lastHorizontalScrollLeftRef: RefObject<number>;
  updateVisibleDateRange: (force?: boolean) => void;
  updateFirstVisibleDate: (force?: boolean) => void;
}

export const usePlannerViewportState = ({
  parentRef,
  cellWidthPx,
  dates,
  isLoading,
  isDragResizeInteracting,
  visibleRowsLength,
  setVisibleDateRange,
  setFirstVisibleDate,
  isDragResizeInteractionActive,
}: UsePlannerViewportStateParams): UsePlannerViewportStateResult => {
  const lastIndexRef = useRef(-1);
  const horizontalWindowRef = useRef<VisibleDateRange>({
    startIndex: 0,
    endIndex: 0,
  });
  const lastHorizontalScrollLeftRef = useRef(-1);

  const updateVisibleDateRange = useCallback(
    (force: boolean = false) => {
      const container = parentRef.current;

      if (!container || cellWidthPx === 0 || dates.length === 0) {
        const resetRange = { startIndex: 0, endIndex: 0 };
        const previousRange = horizontalWindowRef.current;

        if (
          previousRange.startIndex !== resetRange.startIndex ||
          previousRange.endIndex !== resetRange.endIndex
        ) {
          horizontalWindowRef.current = resetRange;
          setVisibleDateRange(resetRange);
        }

        return;
      }

      if (!force && container.dataset.scrollAnimating === 'true') {
        return;
      }

      const nextRange = getNextVisibleDateRange({
        scrollLeft: container.scrollLeft,
        clientWidth: container.clientWidth,
        cellWidthPx,
        datesLength: dates.length,
        previousRange: horizontalWindowRef.current,
      });

      if (!nextRange) {
        return;
      }

      horizontalWindowRef.current = nextRange;
      setVisibleDateRange(nextRange);
    },
    [cellWidthPx, dates.length, parentRef, setVisibleDateRange]
  );

  const updateFirstVisibleDate = useCallback(
    (force: boolean = false) => {
      if (isDragResizeInteractionActive()) {
        return;
      }

      const container = parentRef.current;
      if (!container || cellWidthPx === 0 || isLoading || dates.length === 0) {
        return;
      }

      if (!force && container.dataset.scrollAnimating === 'true') {
        return;
      }

      const nextIndex = clampDateIndex(
        Math.floor(container.scrollLeft / cellWidthPx),
        dates.length
      );
      if (nextIndex === lastIndexRef.current) {
        return;
      }

      lastIndexRef.current = nextIndex;
      setFirstVisibleDate(dates[nextIndex]);
    },
    [cellWidthPx, dates, isDragResizeInteractionActive, isLoading, parentRef, setFirstVisibleDate]
  );

  useEffect(() => {
    if (!isDragResizeInteracting) {
      updateFirstVisibleDate();
    }
  }, [isDragResizeInteracting, updateFirstVisibleDate]);

  useEffect(() => {
    updateVisibleDateRange();
    lastHorizontalScrollLeftRef.current = parentRef.current?.scrollLeft ?? -1;
  }, [parentRef, updateVisibleDateRange, visibleRowsLength]);

  useEffect(() => {
    const handleResize = () => updateVisibleDateRange(false);

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [updateVisibleDateRange]);

  return {
    lastHorizontalScrollLeftRef,
    updateVisibleDateRange,
    updateFirstVisibleDate,
  };
};
